import { useState, useEffect } from 'react';
import { Filter, X, Calendar, Users, Layers, Clock } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GlobalFilters = ({ filters, setFilters, clubId }) => {
  const [options, setOptions] = useState({ teams: [], categories: [], seasons: [] });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    fetchFilterOptions();
  }, [clubId]);

  const fetchFilterOptions = async () => {
    const token = localStorage.getItem('club_token');
    try {
      const res = await axios.get(`${API}/clubs/intelligence/filter-options`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOptions(res.data);
    } catch (err) {
      console.error('Filter options error:', err);
    }
  };

  const updateFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearAll = () => {
    setFilters({ season: '', team_id: '', category: '', date_from: '', date_to: '' });
  };

  const activeCount = [
    filters.season, filters.team_id, filters.category, filters.date_from, filters.date_to
  ].filter(Boolean).length;

  const activeLabel = () => {
    const parts = [];
    if (filters.season) parts.push(filters.season);
    if (filters.category) parts.push(filters.category);
    if (filters.team_id) {
      const t = options.teams.find(t => t.id === filters.team_id);
      if (t) parts.push(t.name);
    }
    if (filters.date_from || filters.date_to) parts.push('Date Range');
    return parts.join(' / ');
  };

  return (
    <div className="mb-4" data-testid="global-filters">
      {/* Filter Toggle Bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={() => setOpen(!open)}
          className={`flex items-center gap-2 px-3 py-2 rounded-sm border text-sm transition-all ${
            activeCount > 0
              ? 'bg-primary/10 border-primary/30 text-primary'
              : 'bg-card border-border/40 text-muted-foreground hover:border-primary/30 hover:text-white'
          }`}
          data-testid="global-filter-toggle"
        >
          <Filter size={14} />
          <span>Filters</span>
          {activeCount > 0 && (
            <span className="ml-1 w-5 h-5 bg-primary text-black rounded-full text-xs flex items-center justify-center font-bold">
              {activeCount}
            </span>
          )}
        </button>

        {activeCount > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-primary/70 truncate max-w-[300px]">{activeLabel()}</span>
            <button
              onClick={clearAll}
              className="text-xs text-muted-foreground hover:text-red-400 flex items-center gap-1 transition-colors"
              data-testid="global-filter-clear"
            >
              <X size={12} /> Clear
            </button>
          </div>
        )}
      </div>

      {/* Expandable Panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="mt-3 bg-card border border-border/40 rounded-sm p-4" data-testid="global-filter-panel">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {/* Season */}
                <FilterField icon={Clock} label="Season">
                  <select
                    value={filters.season}
                    onChange={e => updateFilter('season', e.target.value)}
                    className="w-full bg-zinc-900 border border-border/40 rounded-sm px-2 py-1.5 text-sm outline-none focus:border-primary/50"
                    data-testid="filter-season"
                  >
                    <option value="">All Seasons</option>
                    {options.seasons.map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </FilterField>

                {/* Category */}
                <FilterField icon={Layers} label="Age Category">
                  <select
                    value={filters.category}
                    onChange={e => updateFilter('category', e.target.value)}
                    className="w-full bg-zinc-900 border border-border/40 rounded-sm px-2 py-1.5 text-sm outline-none focus:border-primary/50"
                    data-testid="filter-category"
                  >
                    <option value="">All Categories</option>
                    {options.categories.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </FilterField>

                {/* Team */}
                <FilterField icon={Users} label="Team">
                  <select
                    value={filters.team_id}
                    onChange={e => updateFilter('team_id', e.target.value)}
                    className="w-full bg-zinc-900 border border-border/40 rounded-sm px-2 py-1.5 text-sm outline-none focus:border-primary/50"
                    data-testid="filter-team"
                  >
                    <option value="">All Teams</option>
                    {(filters.category
                      ? options.teams.filter(t => t.category === filters.category)
                      : options.teams
                    ).map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </FilterField>

                {/* Date From */}
                <FilterField icon={Calendar} label="From">
                  <input
                    type="date"
                    value={filters.date_from}
                    onChange={e => updateFilter('date_from', e.target.value)}
                    className="w-full bg-zinc-900 border border-border/40 rounded-sm px-2 py-1.5 text-sm outline-none focus:border-primary/50"
                    data-testid="filter-date-from"
                  />
                </FilterField>

                {/* Date To */}
                <FilterField icon={Calendar} label="To">
                  <input
                    type="date"
                    value={filters.date_to}
                    onChange={e => updateFilter('date_to', e.target.value)}
                    className="w-full bg-zinc-900 border border-border/40 rounded-sm px-2 py-1.5 text-sm outline-none focus:border-primary/50"
                    data-testid="filter-date-to"
                  />
                </FilterField>
              </div>

              <div className="flex justify-end mt-3 pt-3 border-t border-border/20">
                <button
                  onClick={clearAll}
                  className="text-xs text-muted-foreground hover:text-primary transition-colors"
                >
                  Clear All Filters
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const FilterField = ({ icon: Icon, label, children }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
      <Icon size={10} /> {label}
    </label>
    {children}
  </div>
);

export default GlobalFilters;
